import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Calendar,
  FileText,
  DollarSign,
  Building,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  ExternalLink,
} from 'lucide-react';
import { tendersService } from '../../services/api/tendersService';
import { Tender } from '../../types/tenders.types';
import { toast } from 'react-toastify';

const TendersView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tender, setTender] = useState<Tender | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchTender(parseInt(id));
    }
  }, [id]);

  const fetchTender = async (tenderId: number) => {
    try {
      setLoading(true);
      const response = await tendersService.getById(tenderId);
      if (response.success) {
        setTender(response.data);
      } else {
        toast.error('Failed to fetch tender details');
        navigate('/tenders');
      }
    } catch (error) {
      toast.error('Failed to fetch tender details');
      console.error('Error fetching tender:', error);
      navigate('/tenders');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (!amount) return 'N/A';
    return `₹ ${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string): string => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const variants: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <XCircle className="w-4 h-4" /> },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="w-4 h-4" /> },
      awarded: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Award className="w-4 h-4" /> },
    };

    const variant = variants[status] || { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Clock className="w-4 h-4" /> };

    return (
      <span
        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${variant.bg} ${variant.text}`}
      >
        {variant.icon}
        <span className="ml-1.5">{status.toUpperCase()}</span>
      </span>
    );
  };

  const InfoSection: React.FC<{
    label: string;
    value: string | React.ReactNode;
    icon?: React.ReactNode;
    fullWidth?: boolean;
  }> = ({ label, value, icon, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <dt className="flex items-center text-sm font-medium text-gray-500 mb-2">
        {icon && <span className="mr-2 text-gray-400">{icon}</span>}
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading tender details...</p>
        </div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <FileText size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Tender not found</h3>
          <p className="text-gray-600 mb-4">
            The tender you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/tenders')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tender Details</h1>
          <p className="mt-1 text-sm text-gray-600">
            View complete information about this tender
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/tenders/edit/${id}`}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors"
          >
            <Edit size={18} className="mr-2" />
            Edit
          </Link>
          <button
            onClick={() => navigate('/tenders')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header Section */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {tender.title}
              </h2>
              <div className="flex items-center text-sm text-gray-600">
                <FileText size={16} className="mr-2" />
                {tender.tender_number}
              </div>
              {tender.tender_type && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {tender.tender_type}
                  </span>
                </div>
              )}
            </div>
            <StatusBadge status={tender.status} />
          </div>
        </div>

        {/* Details Section */}
        <div className="px-6 py-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tender Number */}
            <InfoSection
              label="Tender Number"
              value={tender.tender_number}
              icon={<FileText className="w-4 h-4" />}
            />

            {/* Tender Type */}
            <InfoSection
              label="Tender Type"
              value={tender.tender_type || 'N/A'}
            />

            {/* Department */}
            <InfoSection
              label="Department"
              value={tender.department || 'N/A'}
              icon={<Building className="w-4 h-4" />}
            />

            {/* Estimated Value */}
            <InfoSection
              label="Estimated Value"
              value={
                <span className="text-lg font-semibold text-green-700">
                  {formatCurrency(tender.estimated_value)}
                </span>
              }
              icon={<DollarSign className="w-4 h-4" />}
            />

            {/* Description */}
            {tender.description && (
              <InfoSection
                label="Description"
                value={
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {tender.description}
                  </p>
                }
                fullWidth
              />
            )}

            {/* Publication Date */}
            <InfoSection
              label="Publication Date"
              value={formatDate(tender.publication_date)}
              icon={<Calendar className="w-4 h-4" />}
            />

            {/* Submission Deadline */}
            <InfoSection
              label="Submission Deadline"
              value={
                <span className="text-red-600 font-semibold">
                  {formatDate(tender.submission_deadline)}
                </span>
              }
              icon={<Calendar className="w-4 h-4" />}
            />

            {/* Opening Date */}
            <InfoSection
              label="Opening Date"
              value={formatDate(tender.opening_date)}
              icon={<Calendar className="w-4 h-4" />}
            />

            {/* Document URL */}
            <InfoSection
              label="Document URL"
              icon={<FileText className="w-4 h-4" />}
              value={
                tender.document_url ? (
                  <a
                    href={tender.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline break-all"
                  >
                    <span className="mr-2">{tender.document_url}</span>
                    <ExternalLink size={14} className="flex-shrink-0" />
                  </a>
                ) : (
                  <span className="text-gray-400">No document attached</span>
                )
              }
              fullWidth
            />

            {/* Display Order */}
            <InfoSection label="Display Order" value={tender.display_order.toString()} />

            {/* Active Status */}
            <InfoSection
              label="Status"
              value={
                tender.is_active ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active - Visible on website
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Inactive - Hidden from website
                  </span>
                )
              }
            />
          </dl>
        </div>

        {/* Metadata Section */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Clock size={16} className="mr-2" />
            Metadata
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500 mb-1">Created At</dt>
              <dd className="text-gray-900">{formatDateTime(tender.created_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-500 mb-1">Last Updated</dt>
              <dd className="text-gray-900">{formatDateTime(tender.updated_at)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Action Buttons (Mobile-friendly) */}
      <div className="flex flex-col sm:flex-row gap-3 sm:hidden">
        <Link
          to={`/tenders/edit/${id}`}
          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors"
        >
          <Edit size={18} className="mr-2" />
          Edit Tender
        </Link>
        <button
          onClick={() => navigate('/tenders')}
          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to List
        </button>
      </div>
    </div>
  );
};

export default TendersView;